import { Controller } from "@hotwired/stimulus"
import { TOAST_KINDS, TOAST_DEFAULT_DURATIONS, TOAST_FALLBACK_DURATION_MS } from "../lib/toast"
import { syncGateReason } from "../lib/gate_reason"

// Canonical toast lifecycle (CSS consolidation Tier 2b). Drives every toast in
// the app — those built by window.showToast, rendered by Rails flash, and
// appended via turbo_stream. Handles the slide-up enter, auto-dismiss, the
// draining progress bar (paused on hover/focus), the close button, and the
// leave animation. Works for both declarative markup and imperative toasts
// (showToast builds the element with this data-controller so Stimulus connects
// it automatically).
//
// #14564 adds three lifecycles on top of the plain countdown:
//
//   • HELD — a toast carrying an interactive action (showToast's `action`, a
//     server-rendered `.gl-toast-action` link/button, or a `.gl-toast-link`)
//     never auto-dismisses. It leaves when acted on, dismissed, or when its
//     explicit domain expiry passes. No action may disappear before the user
//     can reach it by keyboard.
//   • DEADLINE — `data-gl-toast-expires-at-value` (epoch ms, written by
//     showToast from `action.expiresInMs`) is a DOMAIN fact, e.g. the server's
//     undo window. The bar drains toward it and hover/focus do NOT pause it:
//     pausing would tell the user an undo is still possible when it is not.
//     The deadline is checked against the wall clock AT THE CLICK, not only by
//     the timer — a suspended tab or a throttled timer never lets a stale
//     button run its callback past the deadline. At expiry the button reads
//     "Expired"; if the pointer or focus is on the toast it stays put, and a
//     pausable grace countdown runs afterwards.
//   • ACTION — the executable lives on `element.glToastAction` (a property,
//     never markup). It runs single-flight: a second press while in flight is
//     ignored, the close button waits for the outcome (dismissing mid-flight
//     would hide it), and the result is reported truthfully — success re-titles
//     the toast (or dismisses it), failure turns it into an error toast that
//     stays until dismissed. Raw error text is never shown.
//
// ONE lifecycle record, on the element, not on the controller instance:
// `element.glToastLifecycle = { phase, pending, outcome, outcomeRendered,
// remaining }`. Stimulus may disconnect and reconnect a toast (a DOM move, a
// Turbo morph) — with or without the same controller instance — while the
// callback is still pending. The record is what survives that: `phase` is the
// single source of truth (`pending` → `busy` → `settled`, or `pending` →
// `expired`, any → `gone`), so a reconnect can never permit a second run, an
// old promise can only settle the toast whose phase is still `busy`, and an
// outcome that lands while the toast is detached is rendered on reconnect
// rather than lost. Timers, listeners and the animation-frame handle are
// per-connection and are always cleaned up in disconnect().
//
// Keyboard: Escape inside the toast dismisses it (never mid-flight); focus is
// returned to the element the user came from when the toast leaves under them;
// after an outcome that removed the focused button, focus moves to Dismiss.
// Disabled controls explain themselves (BUTTON-4 / A11Y-1): the busy and
// expired action button and the busy-held close button carry the reason as
// their `data-gl-tooltip` and inside their accessible name (gate_reason.js).
// A reconnect is not a fresh toast. Stimulus disconnects and reconnects a
// toast for a data-controller attribute swap as readily as for a DOM move, and
// in the attribute case the element never leaves the page: the pointer can
// still rest on it and the dismiss button can still be document.activeElement.
// No mouseenter or focusin will fire again, so connect() must READ that state
// (the record for hover, the DOM for focus) rather than assume it clear — or it
// would arm a countdown under the user's focus. The focus origin travels on
// the record for the same reason.
const LEAVE_MS = 280 // match the CSS leave transition (~260ms)
const EXPIRED_GRACE_MS = 4000
const KIND_CLASS_PREFIX = "gl-toast--"
const ICON_BASE_CLASS = "ph-fill"
export const PHASES = Object.freeze({ PENDING: "pending", BUSY: "busy", EXPIRED: "expired", SETTLED: "settled", GONE: "gone" })
export const CLOSE_HELD_REASON = "Wait for the action to finish"
const NAME_SEPARATOR = " — "

// The stack is click-through (pointer-events: none) so the page behind stays
// usable; once it holds more than the viewport can show it becomes a bounded,
// scrollable region (see #toast-container in _toast.scss) — and a scrollable
// region must take pointer events for wheel, drag and scrollbar to work. The
// class is the only thing that flips, and it flips both ways.
export function syncStackOverflow(container, { revealNewest = false } = {}) {
  if (!container) return
  const overflowing = container.scrollHeight > container.clientHeight + 1
  container.classList.toggle("is-overflowing", overflowing)
  // A new notification is the one the user must see: keep the stack scrolled
  // to its end (the newest toast, at the corner) when one enters.
  if (revealNewest && overflowing) container.scrollTop = container.scrollHeight
}

function outcomeFrom(result) {
  const isObject = result !== null && typeof result === "object"
  const message = isObject && result.message != null ? String(result.message).trim() || null : null
  if (isObject && result.ok === false) return { ok: false, message }
  return { ok: true, message }
}

export default class extends Controller {
  static values = { duration: Number, expiresAt: Number }

  connect() {
    this.life = this.element.glToastLifecycle ||= {
      phase: PHASES.PENDING, pending: null, outcome: null, outcomeRendered: false, remaining: null,
    }
    this.action = this.element.glToastAction || null
    // Hover and keyboard-focus each independently hold the toast open; the
    // countdown resumes only once BOTH are clear (ref-counted, so a mixed
    // mouse+keyboard interaction can't re-arm while the mouse still rests on it).
    // On a reconnect they are READ, not reset: focus from the DOM (the truth),
    // hover and the focus origin from the record (see the note above).
    this.hovered = Boolean(this.life.hovered)
    this.focused = this.element.contains(document.activeElement)
    this.remaining = null
    this.timer = null
    this.expiryTimer = null
    this.returnFocusTo = this.life.returnFocusTo ?? null

    this.closeButton?.addEventListener("click", this.dismiss)
    this.actionButton?.addEventListener("click", this.onActionClick)
    this.element.addEventListener("mouseenter", this.onMouseEnter)
    this.element.addEventListener("mouseleave", this.onMouseLeave)
    this.element.addEventListener("focusin", this.onFocusIn)
    this.element.addEventListener("focusout", this.onFocusOut)
    this.element.addEventListener("keydown", this.onKeydown)

    this.enterFrame = requestAnimationFrame(() => this.element.classList.add("is-visible"))
    this.resume()
    // Remembered here: by the time disconnect() runs for a removed toast the
    // element has no parent any more, and the stack still needs its re-sync.
    this.stack = this.element.parentElement
    syncStackOverflow(this.stack, { revealNewest: this.life.phase === PHASES.PENDING && !this.life.revealed })
    this.life.revealed = true

    // `focus: true` moves keyboard focus to the action on the toast's FIRST
    // entry only — a reconnect must never steal focus a second time.
    if (this.action?.focus && !this.life.focusTaken && this.life.phase === PHASES.PENDING && this.actionButton) {
      this.life.focusTaken = true
      this.returnFocusTo = document.activeElement
      this.actionButton.focus()
    }
  }

  disconnect() {
    // Bank a running pausable countdown so a reconnect resumes where it left off.
    if (this.timer) {
      this.life.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt))
    } else if (this.remaining != null) {
      this.life.remaining = this.remaining
    }
    // Hover survives only while the element stays in the page (an attribute
    // swap); a detached element cannot be hovered. The focus origin always
    // survives — it is where the user came from, whatever happens to the toast.
    this.life.hovered = this.element.isConnected ? this.hovered : false
    this.life.returnFocusTo = this.returnFocusTo
    this.clear()
    this.clearExpiry()
    if (this.enterFrame) cancelAnimationFrame(this.enterFrame)
    this.closeButton?.removeEventListener("click", this.dismiss)
    this.actionButton?.removeEventListener("click", this.onActionClick)
    this.element.removeEventListener("mouseenter", this.onMouseEnter)
    this.element.removeEventListener("mouseleave", this.onMouseLeave)
    this.element.removeEventListener("focusin", this.onFocusIn)
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.element.removeEventListener("keydown", this.onKeydown)
    syncStackOverflow(this.stack)
  }

  // --- lifecycle ----------------------------------------------------------

  // Pick up the element's phase: a fresh toast starts its countdown; a
  // reconnected one re-renders the state it was in and re-arms only what a
  // connection owns (timers), never re-running the action.
  resume() {
    switch (this.life.phase) {
      case PHASES.PENDING:
        this.startPending()
        break
      case PHASES.BUSY:
        this.renderBusy()
        break
      case PHASES.EXPIRED:
        this.renderExpired()
        this.remaining = this.life.remaining ?? EXPIRED_GRACE_MS
        this.maybeResume()
        break
      case PHASES.SETTLED:
        if (!this.life.outcomeRendered) this.renderOutcome(this.life.outcome)
        else if (this.life.remaining != null) this.startCountdown(this.life.remaining)
        break
      default:
        break
    }
  }

  startPending() {
    const duration = this.hasDurationValue ? this.durationValue : this.defaultDuration()
    const deadline = this.deadline
    const held = !deadline && this.hasInteractiveAction()

    if (deadline) {
      const untilDeadline = deadline - Date.now()
      if (untilDeadline <= 0) {
        this.expire()
        return
      }
      this.setBar(untilDeadline)
      this.expiryTimer = setTimeout(() => this.expire(), untilDeadline)
    } else if (held || duration <= 0) {
      // Persistent toast: no auto-dismiss, no draining bar.
      this.removeBar()
    } else {
      // Track REMAINING time so a hover-pause/resume keeps the JS dismiss timer
      // in lock-step with the CSS progress bar (which pauses/resumes in place
      // via animation-play-state). Re-arming with the full duration would drift
      // the real dismiss past where the bar shows empty on every hover.
      const remaining = this.life.remaining ?? duration
      this.setBar(remaining)
      this.remaining = remaining
      // Arming under an existing hover/focus (a reconnect) must not run the bar
      // either; maybeResume() will not arm the timer, so the bar waits with it.
      if ((this.hovered || this.focused) && this.bar) this.bar.style.animationPlayState = "paused"
      this.maybeResume()
    }
  }

  get deadline() {
    return this.expiresAtValue > 0 ? this.expiresAtValue : null
  }

  // Server-rendered toasts may omit data-gl-toast-duration-value; the kind
  // modifier then picks the same policy table showToast uses.
  defaultDuration() {
    const mod = Array.from(this.element.classList).find((c) => c.startsWith(KIND_CLASS_PREFIX))
    const kind = mod ? mod.slice(KIND_CLASS_PREFIX.length) : "success"
    return TOAST_DEFAULT_DURATIONS[kind] ?? TOAST_FALLBACK_DURATION_MS
  }

  hasInteractiveAction() {
    return Boolean(this.element.querySelector(".gl-toast-action a, .gl-toast-action button, .gl-toast-link"))
  }

  // --- timers ---------------------------------------------------------------

  arm() {
    this.startedAt = Date.now()
    this.timer = setTimeout(() => this.dismiss(), this.remaining)
  }

  clear() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  clearExpiry() {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer)
      this.expiryTimer = null
    }
  }

  get closeButton() {
    return this.element.querySelector(".gl-toast-close")
  }

  get actionButton() {
    return this.element.querySelector(".gl-toast-action-btn")
  }

  get bar() {
    return this.element.querySelector(".gl-toast-progress")
  }

  // Keep the progress bar in sync with the countdown it represents.
  setBar(ms) {
    this.element.style.setProperty("--gl-toast-duration", `${ms}ms`)
  }

  removeBar() {
    this.bar?.remove()
  }

  // (Re)starts a pausable countdown with a fresh bar — used once an action
  // has settled into an ordinary success toast, and on reconnect.
  startCountdown(ms) {
    this.clear()
    this.removeBar()
    const bar = document.createElement("span")
    bar.className = "gl-toast-progress"
    this.element.appendChild(bar)
    this.setBar(ms)
    this.remaining = ms
    if (this.hovered || this.focused) bar.style.animationPlayState = "paused"
    this.maybeResume()
  }

  // Freeze the countdown, banking the time already elapsed so a later resume
  // re-arms with only what's left. Also pauses the progress bar in place. A
  // deadline countdown has no `timer` and is deliberately untouched here.
  pauseCountdown() {
    if (!this.timer) return
    this.clear()
    this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt))
    const bar = this.bar
    if (bar) bar.style.animationPlayState = "paused"
  }

  // Resume only when neither hover nor focus is still holding the toast open.
  maybeResume() {
    if (this.timer || this.hovered || this.focused || this.remaining == null) return
    this.arm()
    const bar = this.bar
    if (bar) bar.style.animationPlayState = "running"
  }

  onMouseEnter = () => { this.hovered = true; this.pauseCountdown() }
  onMouseLeave = () => { this.hovered = false; this.maybeResume() }
  onFocusIn = (event) => {
    this.focused = true
    // Remember where the user came from (a Tab from the page into the toast)
    // so a dismissal under their focus can hand it back rather than drop it
    // on <body>.
    const from = event?.relatedTarget
    if (from && !this.element.contains(from)) this.returnFocusTo = from
    this.pauseCountdown()
  }
  onFocusOut = () => { this.focused = false; this.maybeResume() }
  onKeydown = (event) => {
    if (event.key !== "Escape" || this.life.phase === PHASES.BUSY) return
    event.stopPropagation()
    this.leave()
  }

  // --- action -------------------------------------------------------------

  onActionClick = (event) => {
    event.preventDefault()
    this.runAction()
  }

  runAction() {
    if (!this.action || this.life.phase !== PHASES.PENDING) return
    // Truthful at the click: the wall clock decides, not the timer that may
    // not have fired yet (suspended tab, throttled timers).
    if (this.deadline && Date.now() >= this.deadline) {
      this.expire()
      return
    }

    const life = this.life
    life.phase = PHASES.BUSY
    this.clear()
    this.clearExpiry()
    this.renderBusy()

    const { element, application, identifier } = this
    // The thrown value is deliberately not surfaced: a stack trace or a
    // transport payload is never user copy. Callers wanting their own words
    // resolve { ok: false, message } instead.
    const pending = Promise.resolve()
        .then(() => this.action.run({ toast: element }))
        .then(outcomeFrom, () => ({ ok: false, message: null }))
    life.pending = pending
    pending.then((outcome) => {
      if (life.pending !== pending) return
      life.pending = null
      // A toast that left mid-flight renders nothing: no callback on a removed toast.
      if (life.phase !== PHASES.BUSY) return
      life.phase = PHASES.SETTLED
      life.outcome = outcome
      life.outcomeRendered = false
      // Whichever controller is connected NOW renders it; a detached toast
      // renders on reconnect (resume → SETTLED, outcomeRendered false).
      application.getControllerForElementAndIdentifier(element, identifier)?.renderOutcome(outcome)
    })
  }

  renderBusy() {
    this.element.classList.add("is-busy")
    const button = this.actionButton
    if (button) {
      button.textContent = this.action?.busyLabel ?? "Working…"
      button.setAttribute("aria-busy", "true")
      syncGateReason(button, { ready: false, reason: this.action?.busyReason ?? CLOSE_HELD_REASON })
    }
    this.setCloseHeld(true)
  }

  renderOutcome(outcome) {
    const { ok, message } = outcome
    this.life.outcomeRendered = true
    this.clearExpiry()
    this.element.classList.remove("is-busy")
    this.setCloseHeld(false)
    const hadFocus = this.element.contains(document.activeElement)
    this.element.querySelector(".gl-toast-action")?.remove()
    this.element.querySelector("[data-gl-toast-expiry-note]")?.remove()

    if (ok) {
      const title = message ?? this.action?.successMessage
      if (!title) {
        this.leave({ restoreFocus: hadFocus })
        return
      }
      this.setKind("success")
      this.setTitle(title)
      if (hadFocus) this.closeButton?.focus()
      this.startCountdown(this.life.remaining ?? TOAST_DEFAULT_DURATIONS.success)
    } else {
      this.setKind("danger")
      this.setTitle(message ?? this.action?.failureMessage ?? "That couldn't be completed.")
      this.removeBar()
      this.remaining = null
      this.life.remaining = null
      if (hadFocus) this.closeButton?.focus()
    }
  }

  // The deadline passed. If the action is in flight the outcome decides
  // instead; otherwise the button is retired truthfully and a pausable grace
  // countdown takes over, so the toast never leaves under the pointer or focus.
  expire() {
    this.clearExpiry()
    if (this.life.phase !== PHASES.PENDING) return
    this.life.phase = PHASES.EXPIRED
    this.life.remaining = EXPIRED_GRACE_MS
    this.renderExpired()
    this.remaining = EXPIRED_GRACE_MS
    this.maybeResume()
  }

  renderExpired() {
    this.element.classList.add("is-expired")
    const reason = this.action?.expiredMessage ?? "The time for that has passed."
    const button = this.actionButton
    if (button) {
      button.textContent = this.action?.expiredLabel ?? "Expired"
      button.removeAttribute("aria-busy")
      syncGateReason(button, { ready: false, reason })
    }
    const note = this.action?.expiredMessage
    const body = this.element.querySelector(".gl-toast-body")
    if (note && body && !body.querySelector("[data-gl-toast-expiry-note]")) {
      const line = document.createElement("div")
      line.className = "gl-toast-message"
      line.setAttribute("data-gl-toast-expiry-note", "")
      line.textContent = note
      const title = body.querySelector(".gl-toast-title")
      title ? title.insertAdjacentElement("afterend", line) : body.appendChild(line)
    }
    this.removeBar()
  }

  // The close button is held only while an action is in flight. Its authored
  // name and tooltip ("Dismiss") are kept on the element so a reconnect never
  // composes the reason twice, and are restored exactly on release.
  setCloseHeld(held) {
    const close = this.closeButton
    if (!close) return
    const original = this.element.glToastCloseOriginal ||= {
      name: close.getAttribute("aria-label"),
      tooltip: close.getAttribute("data-gl-tooltip"),
    }
    close.disabled = held
    if (held) {
      close.setAttribute("aria-disabled", "true")
      close.setAttribute("data-gl-tooltip", CLOSE_HELD_REASON)
      close.setAttribute("aria-label", original.name ? `${original.name}${NAME_SEPARATOR}${CLOSE_HELD_REASON}` : CLOSE_HELD_REASON)
      return
    }
    close.removeAttribute("aria-disabled")
    if (original.tooltip == null) close.removeAttribute("data-gl-tooltip")
    else close.setAttribute("data-gl-tooltip", original.tooltip)
    if (original.name == null) close.removeAttribute("aria-label")
    else close.setAttribute("aria-label", original.name)
  }

  setKind(kind) {
    const { mod, icon } = TOAST_KINDS[kind] || TOAST_KINDS.success
    Array.from(this.element.classList)
        .filter((c) => c.startsWith(KIND_CLASS_PREFIX))
        .forEach((c) => this.element.classList.remove(c))
    this.element.classList.add(mod)
    const iconEl = this.element.querySelector(".gl-toast-icon")
    if (iconEl) {
      Array.from(iconEl.classList)
          .filter((c) => c.startsWith("ph-") && c !== ICON_BASE_CLASS)
          .forEach((c) => iconEl.classList.remove(c))
      iconEl.classList.add(icon)
    }
  }

  setTitle(text) {
    const title = this.element.querySelector(".gl-toast-title")
    if (title) title.textContent = text
  }

  // --- leave --------------------------------------------------------------

  dismiss = () => { this.leave() }

  leave({ restoreFocus } = {}) {
    if (this.life.phase === PHASES.BUSY || this.life.phase === PHASES.GONE) return
    this.life.phase = PHASES.GONE
    this.clear()
    this.clearExpiry()
    const restore = restoreFocus ?? this.element.contains(document.activeElement)
    const target = this.returnFocusTo
    this.element.classList.remove("is-visible")
    this.element.classList.add("is-leaving")
    if (restore && target && target.isConnected && typeof target.focus === "function") target.focus()
    setTimeout(() => this.element.remove(), LEAVE_MS) // disconnect() re-syncs the stack
  }
}
